/**
 * 素材URL解決用 React フック
 *
 * - useMaterialUrls: 単一素材用（MaterialPreviewModal 向け）
 * - useGcsSignedUrlMap: 複数素材のGCS署名付きURL一括管理（SimpleMaterialsList 向け）
 */
import { useEffect, useState, useMemo, useRef } from 'react';
import type { TemporaryMaterial, CloudMaterial } from './materialsSlice';
import { fetchMaterialSignedUrl } from './api';
import {
  resolveMaterialUrls,
  isGcsMaterial,
  detectIsTemporary,
  type AnyMaterial,
  type ResolvedMaterialUrls,
} from './materialUrl';

// ---------------------------------------------------------------------------
// 単一素材用フック
// ---------------------------------------------------------------------------

/**
 * 単一素材のURL解決フック。
 * GCS素材の場合は署名付きURLを自動取得し、50分ごとに更新する。
 */
export function useMaterialUrls(
  material: TemporaryMaterial | CloudMaterial | null,
): ResolvedMaterialUrls | null {
  const [gcsSignedUrl, setGcsSignedUrl] = useState<string | null>(null);
  const [signedUrlError, setSignedUrlError] = useState<string | null>(null);

  useEffect(() => {
    if (!material || !isGcsMaterial(material)) {
      setGcsSignedUrl(null);
      setSignedUrlError(null);
      return;
    }

    const isTemp = detectIsTemporary(material);
    let cancelled = false;
    let refreshTimeout: ReturnType<typeof setTimeout> | null = null;

    const fetchUrl = async () => {
      try {
        const url = await fetchMaterialSignedUrl(material.id, isTemp);
        if (!cancelled) {
          setGcsSignedUrl(url);
          setSignedUrlError(null);
          // 50分後にURLを再取得（有効期限は1時間）
          refreshTimeout = setTimeout(() => {
            if (!cancelled) fetchUrl();
          }, 50 * 60 * 1000);
        }
      } catch (error) {
        // 署名付きURL取得失敗を握り潰さず、画面に出せるメッセージとして保持する。
        // 多くはローカルのGCS認証(ADC)切れ。バックエンドのレスポンスbodyを優先表示。
        const detail = extractSignedUrlErrorMessage(error);
        console.error(
          `[useMaterialUrls] GCS署名付きURLの取得に失敗 (material_id=${material.id}, folder=${isTemp ? 'temporary' : 'cloud'}): ${detail}`,
          error,
        );
        if (!cancelled) {
          setGcsSignedUrl(null);
          setSignedUrlError(detail);
        }
      }
    };

    fetchUrl();
    return () => {
      cancelled = true;
      if (refreshTimeout) clearTimeout(refreshTimeout);
    };
  }, [material?.id, material?.storage_bucket, material?.folder_type]);

  return useMemo(() => {
    if (!material) return null;
    return resolveMaterialUrls(material, gcsSignedUrl, signedUrlError);
  }, [material, gcsSignedUrl, signedUrlError]);
}

/** 署名付きURL取得失敗時のエラーから、画面・ログ用の簡潔なメッセージを取り出す */
function extractSignedUrlErrorMessage(error: unknown): string {
  const axiosLike = error as {
    response?: { status?: number; data?: { error?: string } };
    message?: string;
  };
  const status = axiosLike?.response?.status;
  const serverMessage = axiosLike?.response?.data?.error;
  if (status && serverMessage) return `${serverMessage} (HTTP ${status})`;
  if (status) return `署名付きURLの取得に失敗しました (HTTP ${status})`;
  if (serverMessage) return serverMessage;
  return axiosLike?.message || '署名付きURLの取得に失敗しました';
}

// ---------------------------------------------------------------------------
// 複数素材用フック
// ---------------------------------------------------------------------------

/**
 * 複数素材のGCS署名付きURLを一括管理するフック。
 * SimpleMaterialsList のグリッド表示で使用。
 */
// グリッドに大量の素材があっても署名付きURL取得でブラウザの同時接続上限
// (net::ERR_INSUFFICIENT_RESOURCES) を超えないよう、並列数を抑える。
const SIGNED_URL_CONCURRENCY = 6;

export function useGcsSignedUrlMap(
  materials: Array<{ id: string; material: AnyMaterial; isDeleted?: boolean }>,
  activeTab?: string,
): Map<string, string> {
  const [urlMap, setUrlMap] = useState<Map<string, string>>(new Map());
  // 取得済み/取得中の material id を追跡し、重複リクエスト・再フェッチを防ぐ。
  // urlMap を effect 依存に含めると「1件解決→effect再実行→残りを再発行」が
  // 雪だるま式に重複して ERR_INSUFFICIENT_RESOURCES を招くため、ref で管理する。
  const requestedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const pending = materials.filter(
      ({ material, isDeleted }) =>
        !isDeleted && isGcsMaterial(material) && !requestedIdsRef.current.has(material.id),
    );
    if (pending.length === 0) return;

    let cancelled = false;

    const resolveIsTemp = (material: AnyMaterial): boolean => {
      if (material.folder_type !== undefined) return material.folder_type === 'temporary';
      if (activeTab === 'cloud') return false;
      if (activeTab === 'temporary') return true;
      return detectIsTemporary(material);
    };

    const fetchOne = async (material: AnyMaterial) => {
      requestedIdsRef.current.add(material.id);
      try {
        const signedUrl = await fetchMaterialSignedUrl(material.id, resolveIsTemp(material));
        // cancelled 後でも取得済みURLは保存する（requested に残った id を捨てて
        // サムネイルが永久に空になるのを防ぐ）。stale list のワーカー停止は while 側で行う。
        setUrlMap((prev) => new Map(prev).set(material.id, signedUrl));
      } catch (error) {
        // 失敗したものは後で再試行できるよう requested から外す
        requestedIdsRef.current.delete(material.id);
        console.error(`Failed to fetch signed URL for material ${material.id}:`, error);
      }
    };

    // 並列数を SIGNED_URL_CONCURRENCY に制限したワーカープール
    let cursor = 0;
    const worker = async () => {
      while (!cancelled && cursor < pending.length) {
        const { material } = pending[cursor++];
        await fetchOne(material);
      }
    };
    const workerCount = Math.min(SIGNED_URL_CONCURRENCY, pending.length);
    void Promise.all(Array.from({ length: workerCount }, () => worker()));

    return () => {
      cancelled = true;
    };
  }, [materials, activeTab]);

  return urlMap;
}
